<template>
    <div class="gs-products-tabs">
        <div v-if="hasVisibleProducts" class="gs-products-tabs__list">
            <assets-list>
                <template slot="header-left">
                    <label class="gs-products-tabs__search">
                        <span class="sr-only">Search products</span>
                        <input
                            v-model.trim="searchQuery"
                            type="search"
                            placeholder="Search products"
                        >
                    </label>
                </template>
                <template slot="gridContent">
                    <ul role="list" class="gs-products-tabs__grid">
                        <li v-for="(product, index) in filteredProductList" :key="'grid_p_' + index" class="relative">
                            <product-grid-asset :product="product" :admin="isAdmin" :funnel="funnel"
                                :productType="productsTab" @onProductEdit="edit"
                                @onProductDelete="handleDeleteProduct" @onProductCheckOutLinks="openCheckoutPage"
                                @onProductProjection="openProjectionsModal" @onProductTracking="openTrackingModal"
                                @onProductClone="openCloneProduct" @onProductEnableTestMode="openConfirmModal"
                                @onManagePromotools="funnelPromoTools" />
                        </li>
                        <li v-if="productsTab == 'all'" v-for="(bumpproduct, index) in filteredBumps" :key="'grid_b_' + index"
                            class="relative">
                            <product-grid-asset :product="bumpproduct" :admin="isAdmin" :funnel="funnel"
                                productType="atc_order_bumps" @onProductEdit="editBump" @onProductDelete="deleteBump"
                                @onProductCheckOutLinks="openCheckoutPage" @onProductProjection="openProjectionsModal"
                                @onProductTracking="openTrackingModal" @onProductClone="openCloneProduct"
                                @onProductEnableTestMode="openConfirmModal" @onManagePromotools="funnelPromoTools" />
                        </li>
                    </ul>
                </template>
                <template slot="listContent">
                    <div class="gs-products-tabs__rows">
                        <div v-for="(product, index) in filteredProductList" :key="'list_p_' + index">
                            <product-list-asset :product="product" :admin="isAdmin" :productType="productsTab"
                                :funnel="funnel" @onProductEdit="edit" @onProductDelete="handleDeleteProduct"
                                @onProductCheckOutLinks="openCheckoutPage" @onProductProjection="openProjectionsModal"
                                @onProductTracking="openTrackingModal" @onProductClone="openCloneProduct"
                                @onProductEnableTestMode="openConfirmModal" @onManagePromotools="funnelPromoTools" />
                        </div>
                        <div v-if="productsTab == 'all'" v-for="(product, index) in filteredBumps" :key="'list_b_' + index">
                            <product-list-asset :product="product" :admin="isAdmin" productType="atc_order_bumps"
                                :funnel="funnel" @onProductEdit="editBump" @onProductDelete="deleteBump"
                                @onProductCheckOutLinks="openCheckoutPage" @onProductProjection="openProjectionsModal"
                                @onProductTracking="openTrackingModal" @onProductClone="openCloneProduct"
                                @onProductEnableTestMode="openConfirmModal" @onManagePromotools="funnelPromoTools" />
                        </div>
                    </div>
                </template>
            </assets-list>
        </div>
        <div v-else class="gs-products-tabs__empty">
            <h2>{{ searchQuery ? 'No products match your search' : 'No products in this view' }}</h2>
            <p>{{ searchQuery ? 'Try another product name, internal name, or description.' : $t('frontend_product_no_frontend_products') }}</p>
        </div>
        <confirm-modal
            name="confirm-test-mode-modal"
            :confirm-message="confirmMessage"
            :message-content="messageContent"
            button-size="auto"
            @confirm="enableTestModeProduct"
        />
    </div>
</template>
<script>
import AssetsList from '@/components/AssetsList';
import ProductGridAsset from '@/components/ProductGridAsset';
import ProductListAsset from '@/components/ProductListAsset';
import axios from 'axios';

export default {
    components: {
        AssetsList,
        ProductGridAsset,
        ProductListAsset
    },
    props: {
        funnel: {
            type: Object,
            required: true
        },
        products: {
            type: Array,
            required: true
        },
        bumps: {
            type: Array,
            required: true
        },
        isAdmin: {
            required: true
        },
        productsTab: {
            type: String,
            required: true
        },
       
        deleteProduct: {
            type: Function,
            required: true
        },
        deleteBump: {
            type: Function,
            required: true
        },
        openProjectionsModal: {
            type: Function,
            required: true
        },
        openTrackingModal: {
            type: Function,
            required: true
        },
        openCloneProduct: {
            type: Function,
            required: true
        },

    },
    data() {
        return {
            productId: null,
            status: null,
            confirmMessage: '',
            messageContent: '',
            searchQuery: ''
        };
    },
    computed: {
        gridStyle() {
            return {
                gridTemplateColumns: 'repeat(auto-fill, minmax(18rem, 1fr))',
            };
        },
        hasVisibleProducts() {
            return this.filteredProductList.length || this.filteredBumps.length;
        },
        filteredProductList() {
            return this.filterProducts(this.productsForCurrentTab);
        },
        filteredBumps() {
            if (this.productsTab != 'all') return [];

            return this.filterProducts(this.bumps);
        },
        productsForCurrentTab() {
            switch (this.productsTab) {
                case 'all':
                    return this.products;
                case 'frontend':
                    return this.products.filter(product => product.type === 1);
                case 'upsell_downsell':
                    return this.products.filter(product => product.type === 2);
                case 'order_bumps':
                    return this.products.filter(product => product.type === 3);
                case 'atc_order_bumps':
                    return this.bumps;
                default:
                    return [];
            }
        },
    },
    methods: {
        filterProducts(products) {
            const query = this.searchQuery.toLowerCase();

            if (!query) return products;

            return products.filter((product) => {
                const searchable = [
                    product.name,
                    product.internal_name,
                    product.description,
                    product.slug,
                    product.price_point_name,
                ].filter(Boolean).join(' ').toLowerCase();

                return searchable.includes(query);
            });
        },
        funnelPromoTools(funnel_id) {
            this.$router.push({
                name: 'funnel_resources',
                params: { id: funnel_id },
            });
        },
        openConfirmModal(id, status) {
            this.productId = id;
            this.status = status;
            this.confirmMessage = status
                ? this.$t('frontend_groovesell_menu_items_enable_test_mode')
                : this.$t('frontend_groovesell_menu_items_disable_test_mode');
            this.messageContent = status
                ? this.$t('frontend_groovesell_swal_enable_test_mode_status_true_text')
                : this.$t('frontend_groovesell_swal_enable_test_mode_status_false_text');
            this.$root.$emit('modal-open', 'confirm-test-mode-modal');
        },
        async enableTestModeProduct() {
            await axios.post('make-product-in-test-mode', {
                product_id: this.productId,
                is_test_mode_enabled: this.status,
            });

            this.products.map((v, i) => {
                if (v.id === this.productId) {
                    this.products[i].is_test_mode_enabled = this.status;
                }
            });
        },
        openCheckoutPage(productId) {
            this.$router.push({
                name: 'productsCheckoutLinks',
                params: {
                    id: this.funnel.id,
                    product_id: productId,
                },
            });
        },

        edit(productId, is_complete, tab) {
            const name = (this.productsTab == 'atc_order_bumps' || tab == 'atc_order_bumps') ? 'edit_bump' : 'edit_product';
            const params = { id: this.funnel.id, product_id: productId };
            const query = is_complete ? { validate: 1 } : {};
            this.$router.push({ name, params, query });
        },
        editBump(productId, is_complete) {
            this.edit(productId, is_complete, 'atc_order_bumps');
        },
        handleDeleteProduct(product, is_complete) {
            if (this.productsTab == 'atc_order_bumps') {
                this.deleteBump(product, is_complete);
            } else {
                this.deleteProduct(product, is_complete);
            }
        }
    }
}
</script>

<style lang="scss">
.gs-products-tabs {
    width: 100%;
}

.gs-products-tabs__list {
    margin-top: 18px;
}

.gs-products-tabs__search {
    display: block;
    width: min(360px, 100vw - 48px);
    margin: 0;
}

.gs-products-tabs__search input {
    width: 100%;
    min-height: 42px;
    padding: 0 14px;
    color: var(--gs-foreground, #182230);
    font-size: 14px;
    font-weight: 600;
    background: #ffffff;
    border: 1px solid #dfe4ee;
    border-radius: 8px;
    outline: none;
    transition: border-color .15s ease, box-shadow .15s ease;
}

.gs-products-tabs__search input:focus {
    border-color: #ff8aa2;
    box-shadow: 0 0 0 3px rgba(255, 91, 127, .12);
}

.gs-products-tabs__search input::placeholder {
    color: #9aa1b7;
}

.gs-products-tabs__grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    gap: 16px 24px;
    padding: 0;
    margin: 0;
    list-style: none;
}

.gs-products-tabs__rows {
    display: grid;
    gap: 10px;
}

.gs-products-tabs__empty {
    margin-top: 28px;
    padding: 42px;
    text-align: center;
    background: #ffffff;
    border: 1px solid var(--gs-border, #e2e8f0);
    border-radius: 8px;
    box-shadow: 0 14px 32px rgba(31, 37, 72, .05);
}

.gs-products-tabs__empty h2 {
    margin: 0 0 8px;
    color: var(--gs-foreground, #182230);
    font-size: 22px;
    font-weight: 700;
}

.gs-products-tabs__empty p {
    margin: 0;
    color: #6c748e;
}
</style>
